import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Mail, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/landing/Header';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const APP_URL = 'https://app.lindberghai.com';
const HOLDING_TIPS_URL = 'https://lindberghai.com/holding-tips';

const Landing = () => {
  const navigate = useNavigate();

  const scrollToPricing = () =>
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });

  const CheckIcon = () => (
    <svg
      className="h-5 w-5 text-primary flex-shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Subtle grid background */}
      <div
        className="absolute inset-0 w-full pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgb(100 100 100 / 0.03) 1px, transparent 1px),
            linear-gradient(to right, rgb(100 100 100 / 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          zIndex: 0,
        }}
      />

      <Header />

      <main className="flex-1 pt-[72px] relative z-10">

        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-6 md:px-8">
            <div className="mx-auto max-w-[64rem] space-y-6 text-center">
              <span className="inline-flex items-center rounded-full border border-gray-200 px-3 py-1 text-sm text-muted-foreground">
                <span className="mr-2 h-1.5 w-1.5 rounded-full bg-primary animate-flicker" />
                <b>Built by pilots</b>&nbsp;<span>for pilots</span>
              </span>

              <h1 className="text-4xl font-medium tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
                Stop searching.
                <br />
                <span className="bg-gradient-to-r from-primary/60 to-primary bg-clip-text text-transparent">
                  Start finding answers—faster and smarter.
                </span>
              </h1>

              <p className="mx-auto max-w-xl text-lg text-muted-foreground font-light leading-relaxed">
                Ask anything and get answers cited straight from FAA handbooks, Advisory
                Circulars, and the FAR/AIM — so you study faster and always know exactly
                where to look.
              </p>

              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row pt-2">
                <Button
                  onClick={() => window.open(APP_URL, '_blank')}
                  className="inline-flex h-11 items-center justify-center rounded-full bg-primary px-8 text-sm font-medium text-primary-foreground hover:bg-primary/90 group"
                >
                  Try for Free
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })
                  }
                  className="inline-flex h-11 items-center justify-center rounded-full border border-gray-200 px-8 text-sm font-medium hover:bg-gray-100"
                >
                  How it works
                </Button>
              </div>
            </div>

            {/* Hero screenshot */}
            <div className="mx-auto mt-12 max-w-3xl">
              <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-xl">
                <img
                  src="/hero-chat.png"
                  alt="LindberghAI chat interface"
                  className="w-full h-auto block"
                />
              </div>
            </div>

            {/* Partnership strip */}
            <div className="mx-auto mt-10 max-w-3xl flex flex-col items-center gap-4">
              <p className="text-sm text-muted-foreground uppercase tracking-widest font-medium">
                Proudly in partnership with
              </p>
              <img
                src="/ppot-logo.png"
                alt="Professional Pilots of Tomorrow"
                className="w-[40%] h-auto"
              />
            </div>
          </div>
        </section>

        {/* ── Features strip ────────────────────────────────────────────── */}
        <section className="bg-gray-50 py-14">
          <div className="container mx-auto px-6 md:px-8">
            <div className="mx-auto max-w-[64rem]">
              <div className="mb-8 space-y-2">
                <p className="text-sm text-muted-foreground uppercase tracking-wide">
                  How does Lindbergh help?
                </p>
                <h2 className="text-3xl md:text-4xl font-medium tracking-tight">
                  FAA data only. Cited sources. Every time.
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl">
                  LindberghAI is trained on FAA handbooks, Advisory Circulars, the FAR/AIM,
                  and CFRs—so the answers you get come from the sources that matter.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6">
                <div className="space-y-1">
                  <h3 className="font-semibold text-foreground">FAA-data only</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    We focus on the publications you&apos;re expected to know—so explanations
                    stay aligned with official material.
                  </p>
                </div>
                <div className="space-y-1">
                  <h3 className="font-semibold text-foreground">Prepare for your checkride</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Part of the ride is showing you know where to look. Lindbergh cites its FAA
                    sources so you can review and bookmark the reference.
                  </p>
                </div>
                <div className="space-y-1">
                  <h3 className="font-semibold text-foreground">Save time</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Spend less time flipping through handbooks and more time studying, resting, or
                    doing something fun—burnout helps nobody.
                  </p>
                </div>
                <div className="space-y-1">
                  <h3 className="font-semibold text-foreground">Anytime, anywhere</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Instead of texting your CFI while they&apos;re flying—or sleeping—ask Lindbergh
                    and get a sourced clarification right away.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── How it works ──────────────────────────────────────────────── */}
        <section id="how-it-works" className="py-14">
          <div className="container mx-auto px-6 md:px-8">
            <div className="mx-auto max-w-[64rem]">
              <div className="space-y-4 text-center mb-12">
                <h2 className="text-4xl font-medium">How it works</h2>
                <p className="text-xl text-muted-foreground">
                  Two focused tools: your FAA-grounded study partner, plus holding practice.
                </p>
                <div className="flex justify-center gap-3 pt-1">
                  <Button
                    variant="outline"
                    className="rounded-full px-6"
                    onClick={() => window.open(APP_URL, '_blank')}
                  >
                    Try for Free
                  </Button>
                  <Button variant="outline" className="rounded-full px-6" onClick={scrollToPricing}>
                    Choose your plan
                  </Button>
                </div>
              </div>

              <div className="space-y-16">
                {/* LindberghAI */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                  <div className="space-y-4 order-2 md:order-1">
                    <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      Tool 01
                    </div>
                    <h3 className="text-3xl font-medium">LindberghAI</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Ask Lindbergh anything—&ldquo;What is Bernoulli&apos;s principle?&rdquo; or
                      &ldquo;What are the factors that affect stall speed?&rdquo; Trained on FAA
                      handbooks, Advisory Circulars, and related documents, it delivers
                      comprehensive, technically correct answers.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      Crucially, Lindbergh cites its sources so your learning meets a high standard:
                      review and bookmark the reference whenever you need it.
                    </p>
                    <button
                      type="button"
                      className="inline-flex items-center text-sm font-medium text-foreground hover:text-primary transition-colors group"
                      onClick={() => window.open(APP_URL, '_blank')}
                    >
                      Open LindberghAI
                      <ArrowUpRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  </div>
                  <div className="order-1 md:order-2">
                    <div className="rounded-xl overflow-hidden border border-gray-200 shadow-md">
                      <img
                        src="/demo-chat.png"
                        alt="LindberghAI answering a question about BasicMed with FAA citations"
                        className="w-full h-auto block"
                      />
                    </div>
                  </div>
                </div>

                {/* Holding Copilot */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                  <div>
                    <div className="rounded-xl overflow-hidden border border-gray-200 shadow-md">
                      <img
                        src="/demo-holding.png"
                        alt="Holding Copilot decoding a holding clearance"
                        className="w-full h-auto block"
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      Tool 02
                    </div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-3xl font-medium">Holding Copilot</h3>
                      <span className="text-xs font-medium bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                        Beta
                      </span>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      Holding Copilot can generate VOR, GPS, and DME holds on demand. It uses
                      real-world fixes so you can practice receiving an instruction and deciphering
                      the clearance.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      Start with the full lesson plan on holding fundamentals on our Holding Tips
                      page to get the most from your sessions.
                    </p>
                    <button
                      type="button"
                      className="inline-flex items-center text-sm font-medium text-foreground hover:text-primary transition-colors group"
                      onClick={() => window.open(HOLDING_TIPS_URL, '_blank')}
                    >
                      Holding Tips
                      <ArrowUpRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Testimonials ──────────────────────────────────────────────── */}
        <section className="bg-gray-50 py-14 overflow-hidden">
          <div className="container mx-auto px-6 md:px-8 mb-8">
            <div className="mx-auto max-w-[64rem]">
              <h2 className="text-3xl font-medium text-center">What instructors are saying</h2>
            </div>
          </div>

          <div className="relative w-full overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none" />
            <div className="flex animate-scroll">
              {[0, 1].map((set) => (
                <div key={set} className="flex gap-6 min-w-full px-3">
                  <div className="w-[min(100%,420px)] shrink-0 rounded-2xl border border-gray-200 bg-white p-8 space-y-4 shadow-sm">
                    <p className="text-muted-foreground leading-relaxed">
                      &ldquo;Lindbergh is awesome. It helps me by igniting my creativity for new
                      scenarios and ideas, keeping my lessons more exciting and fresh for my
                      students.&rdquo;
                    </p>
                    <div>
                      <p className="font-medium">Jack</p>
                      <p className="text-sm text-muted-foreground">CFI</p>
                    </div>
                  </div>
                  <div className="w-[min(100%,420px)] shrink-0 rounded-2xl border border-gray-200 bg-white p-8 space-y-4 shadow-sm">
                    <p className="text-muted-foreground leading-relaxed">
                      &ldquo;This tool helps save my students both time and money as they can learn
                      effectively while away from the classroom, and come to lessons more prepared
                      than ever.&rdquo;
                    </p>
                    <div>
                      <p className="font-medium">Shawn</p>
                      <p className="text-sm text-muted-foreground">CFI</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Pricing ───────────────────────────────────────────────────── */}
        <section id="pricing" className="py-14">
          <div className="container mx-auto px-6 md:px-8">
            <div className="mx-auto max-w-[64rem] space-y-8 text-center">
              <div className="space-y-3">
                <h2 className="text-4xl font-medium">Choose your plan</h2>
                <p className="text-lg text-muted-foreground">
                  Flexible pricing built to grow with you—monthly flexibility or annual savings.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mt-10 text-left">
                {/* Member */}
                <div className="rounded-xl border border-gray-200 bg-white p-8 flex flex-col">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-2xl font-medium">Member</h3>
                      <p className="text-muted-foreground mt-1">Get started at no cost</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-5xl font-medium">$0</p>
                      <p className="text-muted-foreground">per month</p>
                      <p className="text-sm invisible select-none" aria-hidden="true">&nbsp;</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => window.open(APP_URL, '_blank')}
                    className="inline-flex h-11 w-full items-center justify-center rounded-full mt-6"
                  >
                    Try Free
                  </Button>
                  <ul className="space-y-3 pt-6">
                    {['10 uses (chats) per month', 'Explore LindberghAI', 'Upgrade anytime'].map(
                      (f) => (
                        <li key={f} className="flex items-start gap-3 text-sm text-muted-foreground">
                          <CheckIcon />
                          <span>{f}</span>
                        </li>
                      ),
                    )}
                  </ul>
                </div>

                {/* Aviator */}
                <div className="rounded-xl border border-gray-200 bg-white p-8 flex flex-col">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-2xl font-medium">Aviator</h3>
                      <p className="text-muted-foreground mt-1">Pay-as-you-go flexibility</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-5xl font-medium">$10</p>
                      <p className="text-muted-foreground">per month</p>
                      <p className="text-sm invisible select-none" aria-hidden="true">&nbsp;</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => window.open(APP_URL, '_blank')}
                    className="inline-flex h-11 w-full items-center justify-center rounded-full bg-primary text-primary-foreground mt-6"
                  >
                    Start Monthly
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <ul className="space-y-3 pt-6">
                    {[
                      'Unlimited uses per month',
                      'Easy month-to-month flexibility',
                      'Cancel anytime, no questions asked',
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-muted-foreground">
                        <CheckIcon />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Annual */}
                <div className="rounded-xl border-2 border-primary bg-white p-8 relative flex flex-col">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                      Most popular
                    </span>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-2xl font-medium">Annual — Aviator</h3>
                      <p className="text-muted-foreground mt-1">Our best value</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-5xl font-medium">$75</p>
                      <p className="text-muted-foreground">per year</p>
                      <p className="text-sm text-primary font-medium">That&apos;s just $6.25/month</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => window.open(APP_URL, '_blank')}
                    className="inline-flex h-11 w-full items-center justify-center rounded-full bg-primary text-primary-foreground mt-6"
                  >
                    Save 37% — Go Annual
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <ul className="space-y-3 pt-6">
                    {[
                      'Everything in Monthly, at a discounted rate',
                      'Peace of mind with a single annual payment',
                      'Cancel anytime, no questions asked',
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-muted-foreground">
                        <CheckIcon />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <p className="text-sm text-muted-foreground max-w-2xl mx-auto pt-2">
                Are you a PPOT member? Visit &ldquo;Member Resources&rdquo; on{' '}
                <a
                  href="https://theppot.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground underline underline-offset-2 hover:text-primary"
                >
                  thePPOT.org
                </a>{' '}
                for pricing details, then use Try for Free above.
              </p>

              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Shield className="h-4 w-4" />
                Subscriptions keep the lights on—thanks for supporting LindberghAI
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────────────── */}
        <section id="faq" className="bg-gray-50 py-14">
          <div className="container mx-auto px-6 md:px-8">
            <div className="mx-auto max-w-3xl space-y-8">
              <div className="text-center space-y-3">
                <h2 className="text-4xl font-medium">Frequently asked questions</h2>
                <p className="text-muted-foreground">
                  Answers about how LindberghAI supports your training and checkride prep.
                </p>
              </div>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1">
                  <AccordionTrigger>How does LindberghAI enhance learning?</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    LindberghAI uses advanced AI to simplify complex aviation concepts with clear
                    explanations—and it cites its references so you know where to find the source,
                    which matters on checkride day.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger>Is Lindbergh free?</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    Lindbergh is free for your first 10 queries (chats). After that, a subscription
                    unlocks unlimited use so we can keep improving the product.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3">
                  <AccordionTrigger>
                    Does Lindbergh replace ground school or my instructor?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    No. Lindbergh does not replace an FAA ground school or your certificated flight
                    instructor. It helps break down concepts and pull together topics that may be
                    spread across publications and chapters.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-4">
                  <AccordionTrigger>What sources does LindberghAI use?</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    LindberghAI is built on FAA source materials—handbooks, Advisory Circulars, the
                    FAR/AIM, and CFRs—so summaries and explanations stay tied to official references.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>

        {/* ── Footer ────────────────────────────────────────────────────── */}
        <footer className="bg-white border-t border-gray-200">
          <div className="container mx-auto px-6 md:px-8 py-10">
            <div className="flex flex-col items-center gap-6">
              <a href="/" className="flex items-center gap-2">
                <img
                  src="/logo-icon.png"
                  alt="LindberghAI icon"
                  className="h-6 w-auto"
                />
                <span className="text-xl font-semibold tracking-tight text-foreground">
                  Lindbergh<span className="text-primary">AI</span>
                </span>
              </a>
              <div className="flex flex-nowrap items-center justify-center gap-6 md:gap-8 text-sm text-muted-foreground">
                <button
                  type="button"
                  onClick={() => navigate('/legal')}
                  className="shrink-0 hover:text-foreground transition-colors"
                >
                  Legal
                </button>
                <button
                  type="button"
                  onClick={() => window.open(APP_URL, '_blank')}
                  className="shrink-0 hover:text-foreground transition-colors"
                >
                  Try for Free
                </button>
                <button
                  type="button"
                  onClick={scrollToPricing}
                  className="shrink-0 hover:text-foreground transition-colors"
                >
                  Pricing
                </button>
                <button
                  type="button"
                  onClick={() =>
                    document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })
                  }
                  className="shrink-0 hover:text-foreground transition-colors"
                >
                  FAQ
                </button>
                <a
                  href="mailto:info@lindberghai.com"
                  className="shrink-0 hover:text-foreground transition-colors flex items-center gap-1.5"
                >
                  <Mail className="h-4 w-4" />
                  info@lindberghai.com
                </a>
              </div>
            </div>
          </div>
        </footer>

      </main>
    </div>
  );
};

export default Landing;
